import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PencilSimple, Warning, ArrowUUpLeft, ShieldCheck, Archive, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConsentProofButton } from "@/components/leads/ConsentProofButton";
import { LeadTestButton, testMarkNote } from "@/components/leads/MarkTestDialog";

/**
 * Lead detail hero: back button, name/status pills, and top-right actions.
 * Iter-63.1 — also owns Archive / Restore / Super Admin Purge controls.
 */
export default function LeadHeader({
  lead, canEdit, canReport, canExport, editing, onStartEdit, onChanged,
}) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [purgeState, setPurgeState] = useState(null);   // {preview, confirm, reason}
  const isSuperAdmin = user?.role === "super_admin";

  const downloadJson = async () => {
    const r = await api.post(`/leads/${lead.id}/export`);
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-${lead.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTcpaDossier = async () => {
    const t = toast.loading("Building TCPA dossier (rendering page snapshot)…");
    try {
      const resp = await api.get(`/leads/${lead.id}/tcpa-dossier`, {
        responseType: "blob",
      });
      const cd = resp.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `tcpa-dossier-${lead.id.slice(0, 8)}.zip`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("TCPA dossier downloaded", { id: t });
    } catch (err) {
      toast.error(err.response?.data?.detail || "TCPA dossier failed", { id: t });
    }
  };

  const archiveLead = async () => {
    if (!confirm("Archive this lead? Archived leads are hidden from Active views. Downstream audit / billing / delivery records are preserved.")) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/archive`);
      toast.success("Lead archived");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    } finally {
      setBusy(false);
    }
  };
  const restoreLead = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/restore`);
      toast.success("Lead restored");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };
  const openPurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/leads/${lead.id}/purge-preview`);
      setPurgeState({ preview: data, confirm: "", reason: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge preview failed");
    } finally {
      setBusy(false);
    }
  };
  const submitPurge = async () => {
    if (!purgeState?.preview) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/purge`, {
        confirm: purgeState.confirm,
        reason: purgeState.reason,
      });
      toast.success(data?.mode === "anonymised" ? "Lead anonymised (financial history retained)" : "Lead hard-deleted");
      setPurgeState(null);
      if (data?.mode === "hard_deleted") nav("/leads");
      else onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => nav(-1)} className="label-mono flex items-center gap-1 mb-3 hover:underline" data-testid="back-btn">
        <ArrowUUpLeft size={12} /> Back
      </button>
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="label-mono">Lead · <span className="font-mono">{lead.id}</span></div>
          <h1 className="font-chivo font-black text-4xl uppercase tracking-tighter" data-testid="lead-title">
            {lead.first_name} {lead.last_name}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={lead.status} testId="lead-status" />
            <StatusBadge status={lead.delivery_status} testId="lead-delivery-status" />
            {lead.is_test && (
              <span className="text-[10px] bg-yellow-400 text-black px-2 py-0.5 uppercase tracking-widest border border-black">Test</span>
            )}
            {lead.test_mark && (
              <span className="text-[10px] text-zinc-600" data-testid="lead-test-mark-note">{testMarkNote(lead.test_mark)}</span>
            )}
            {lead.duplicate_of && (
              <span className="text-[10px] bg-yellow-400 text-black px-2 py-0.5 uppercase tracking-widest border border-black">
                Duplicate of {lead.duplicate_of.slice(0, 8)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {canEdit && !editing && (
            <button onClick={onStartEdit} data-testid="edit-lead-btn"
              className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100">
              <PencilSimple size={12} className="inline mr-1" /> Edit
            </button>
          )}
          {canReport && (
            <button onClick={() => nav(`/leads/${lead.id}/report`)} data-testid="report-bad-btn"
              className="border border-red-600 text-red-700 px-3 py-2 text-xs uppercase tracking-widest hover:bg-red-600 hover:text-white transition-colors duration-100">
              <Warning size={12} className="inline mr-1" /> Report bad lead
            </button>
          )}
          <ConsentProofButton leadId={lead.id} />
          {canExport && (
            <>
              <button onClick={downloadTcpaDossier} data-testid="tcpa-dossier-btn"
                title="Download ZIP with TrustedForm cert, IP, user-agent, verbatim consent, timestamp + landing page PNG"
                className="border-2 border-emerald-700 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs uppercase tracking-widest hover:bg-emerald-700 hover:text-white transition-colors duration-100 inline-flex items-center gap-1">
                <ShieldCheck size={12} weight="bold" /> TCPA Dossier
              </button>
              <button onClick={downloadJson} data-testid="export-lead-btn"
                className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100">
                Export
              </button>
            </>
          )}
          {canEdit && <LeadTestButton lead={lead} onChanged={onChanged} />}
          {canEdit && !lead.is_archived && (
            <button onClick={archiveLead} disabled={busy} data-testid="archive-lead-btn"
              className="border-2 border-amber-700 bg-amber-50 text-amber-900 px-3 py-2 text-xs uppercase tracking-widest hover:bg-amber-700 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
              <Archive size={12} weight="bold" /> Archive
            </button>
          )}
          {canEdit && lead.is_archived && (
            <button onClick={restoreLead} disabled={busy} data-testid="restore-lead-btn"
              className="border-2 border-emerald-700 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs uppercase tracking-widest hover:bg-emerald-700 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
              <ArrowCounterClockwise size={12} weight="bold" /> Restore
            </button>
          )}
          {isSuperAdmin && (
            <button onClick={openPurge} disabled={busy} data-testid="purge-lead-btn"
              title="Super-admin hard purge — asks for a typed confirmation token"
              className="border-2 border-red-700 bg-red-50 text-red-900 px-3 py-2 text-xs uppercase tracking-widest hover:bg-red-700 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
              <Trash size={12} weight="bold" /> Super-admin purge
            </button>
          )}
        </div>
      </div>

      {purgeState && (
        <div className="fixed inset-0 z-40 flex items-start justify-center pt-16 bg-black/50"
             data-testid="purge-lead-modal">
          <div className="bg-white border-2 border-red-700 max-w-lg w-full mx-4 p-5">
            <div className="font-chivo font-black uppercase tracking-tighter mb-3 text-red-900">
              Hard purge lead {lead.id.slice(0, 8)}
            </div>
            <div className="text-xs text-zinc-800 mb-4 leading-relaxed">
              This is a <b>super-admin destructive action</b>. When downstream
              financial records exist (invoice lines, replacements, returns)
              the lead will be <b>anonymised in place</b> to preserve billing
              integrity — otherwise it will be <b>hard-deleted</b> along with
              its delivery attempts, lead events, and revisions.
            </div>
            <div className="text-xs mb-3">
              <div>Recommended: <b>{purgeState.preview.recommended_action}</b></div>
              <div>Financial deps: <b>{purgeState.preview.has_financial_dependencies ? "YES (will anonymise)" : "NO (will hard-delete)"}</b></div>
              <div>Delivery attempts: <b>{purgeState.preview.dependencies?.delivery_attempts ?? 0}</b></div>
              <div>Lead events: <b>{purgeState.preview.dependencies?.events ?? 0}</b></div>
              <div>Revisions: <b>{purgeState.preview.dependencies?.revisions ?? 0}</b></div>
              <div>Invoice lines: <b>{purgeState.preview.dependencies?.invoice_lines ?? 0}</b></div>
              <div>Replacements: <b>{purgeState.preview.dependencies?.replacements ?? 0}</b></div>
              <div>Returns: <b>{purgeState.preview.dependencies?.returns ?? 0}</b></div>
            </div>
            <label className="label-mono">Reason (audit annotation)</label>
            <textarea rows={2}
              className="w-full border border-black px-2 py-1.5 mt-1 text-sm mb-3"
              placeholder="e.g. Legal request — GDPR erasure ticket 4137."
              value={purgeState.reason}
              onChange={(e) => setPurgeState({ ...purgeState, reason: e.target.value })}
              data-testid="purge-lead-reason" />
            <label className="label-mono">Confirmation token</label>
            <input type="text"
              className="w-full border border-red-700 px-2 py-1.5 mt-1 text-sm font-mono mb-1"
              placeholder={purgeState.preview.confirm_token}
              value={purgeState.confirm}
              onChange={(e) => setPurgeState({ ...purgeState, confirm: e.target.value })}
              data-testid="purge-lead-confirm-input" />
            <div className="text-[10px] text-zinc-600 mb-4">
              Type exactly: <code className="font-mono">{purgeState.preview.confirm_token}</code>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPurgeState(null)} disabled={busy}
                data-testid="purge-lead-cancel"
                className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
                Cancel
              </button>
              <button onClick={submitPurge}
                disabled={busy || purgeState.confirm !== purgeState.preview.confirm_token}
                data-testid="purge-lead-confirm"
                className="bg-red-700 border border-red-700 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
                {busy ? "Purging…" : "Purge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
